import React, { FC, useState } from 'react';
import DetailsTab, {
  DetailsTabKey
} from '../../../../../../clients/sidebar/components/DetailsTab';
import Attachments from '../../../../../../clients/sidebar/views/SidebarPanel/Detailbar/Attachments';
import Severity from '../../../../../../clients/sidebar/views/SidebarPanel/Detailbar/Severity';
import DueDate from '../../../../../../clients/sidebar/views/SidebarPanel/Detailbar/DueDate';
import { Assignees } from '../../../../../../clients/sidebar/views/SidebarPanel/Detailbar/Assignees';
import Tags from '../../../../../../clients/sidebar/views/SidebarPanel/Detailbar/Tags';
import VisibleTo from '../../../../../../clients/sidebar/views/SidebarPanel/Detailbar/VisibleTo';
import TaskLocation from '../../../../../../clients/sidebar/views/SidebarPanel/Detailbar/TaskLocation';
import AdditionalInfo from '../../../../../../clients/sidebar/views/SidebarPanel/Detailbar/AdditionalInfo';
import Integrations from '../../../../components/Integrations';
import { DrawerScreenshot } from './DrawerScreenshot';
import { DrawerComments, DrawerActivityLog } from './DrawerCommentsAndLog';
import { MentionableTask } from '../../../../../../clients/sidebar/views/CreateTask/create_task/types';
import { DashboardTask } from './queries';
import {
  DashboardProjectAccess,
  DashboardProjectContext
} from './projectContextQueries';
import {
  getData,
  setData,
  UA_TASK_DETAILS_ACTIVE_TAB
} from '../../../../models/LocalStorage';
import { getAvailableDashboardTabs } from './getAvailableDashboardTabs';
import { useDrawerIntegrations } from './useDrawerIntegrations';
import styles from './styles.module.css';
import SessionReplay from '../../../../../../clients/shared/SessionReplay';

type Props = {
  task: DashboardTask;
  bugherdUrl: string;
  container: HTMLElement;
  currentUserId: string;
  currentUserRole: string;
  projectContext: DashboardProjectContext | null;
  accessTo: DashboardProjectAccess | null;
  updateTask: (attributes: Record<string, unknown>) => void;
  createTag: (newTagName: string) => void;
  refetchTask: () => void;
  mentionableTasks: MentionableTask[];
  onOpenTask?: (taskId: number) => void;
};

const getStoredActiveTab = (availableTabs: DetailsTabKey[]): DetailsTabKey => {
  const stored = getData(UA_TASK_DETAILS_ACTIVE_TAB);
  if (availableTabs.includes(stored)) return stored;
  return 'taskInfo';
};

export const DrawerTabs: FC<Props> = props => {
  const {
    task,
    bugherdUrl,
    container,
    currentUserId,
    currentUserRole,
    projectContext,
    accessTo,
    updateTask,
    createTag,
    refetchTask,
    mentionableTasks,
    onOpenTask
  } = props;

  const [activeTab, setActiveTab] = useState<DetailsTabKey>(
    () => getData(UA_TASK_DETAILS_ACTIVE_TAB) || 'taskInfo'
  );
  const {
    showIntegrationsTab,
    connectedIntegrations,
    harvestIntegration,
    harvestEmbedUrl,
    taskConnectionData,
    webIntegrations,
    projectId,
    organizationId,
    taskId,
    taskLocalId
  } = useDrawerIntegrations({
    task,
    projectContext,
    currentUserId,
    bugherdUrl
  });

  const showAttachments = !accessTo?.attachmentsDisabled;
  const availableTabs = getAvailableDashboardTabs({
    showAttachments,
    showIntegrationsTab
  });
  const resolvedTab = availableTabs.includes(activeTab)
    ? activeTab
    : getStoredActiveTab(availableTabs);
  const handleTabChange = (tab: DetailsTabKey) => {
    setActiveTab(tab);
    setData(UA_TASK_DETAILS_ACTIVE_TAB, tab);
  };

  if (!accessTo) return null;

  const projectUsers = (projectContext?.users || []).map(user => ({
    ...user,
    id: Number(user.id)
  }));
  const assignedUserIds = task.assignedUserIds || [];
  const assignees = projectUsers.filter(user =>
    assignedUserIds.includes(String(user.id))
  );
  const assignableUsers = projectUsers.filter(
    user => !assignedUserIds.includes(String(user.id))
  );

  const allTags = (projectContext?.tags || []).map(tag => ({
    ...tag,
    id: tag.id != null ? Number(tag.id) : tag.id,
    display: tag.name,
    taskCount: tag.taskCount ?? undefined
  }));
  const taskTagIds: (number | null)[] = (task.tagIds || []).map(Number);
  const tags = allTags.filter(tag => taskTagIds.includes(tag.id));

  const hasGuestWatchers = (task.watcherIds || []).some(watcherId =>
    projectUsers.some(
      user => String(user.id) === String(watcherId) && user.role === 'guest'
    )
  );

  const showLocation = task.kind !== 'admin' && !!task.site && !!task.url;
  const absoluteUrl = task.site ? `${task.site}${task.url}` : task.url || '';
  const isRecordingScreenshot =
    !!task.screenshotUrl &&
    (task.screenshotUrl.endsWith('.webm') ||
      task.screenshotUrl.endsWith('.mp4'));

  return (
    <>
      <DrawerScreenshot
        task={task}
        bugherdUrl={bugherdUrl}
        container={container}
        currentUserId={currentUserId}
        currentUserRole={currentUserRole}
        projectContext={projectContext}
        accessTo={accessTo}
        updateTask={updateTask}
      />
      <SessionReplay
        available={task.sessionReplayAvailable}
        apiDomain={bugherdUrl}
        projectId={Number(task.project.id)}
        taskId={Number(task.id)}
      />

      <div className={styles.detailsTab}>
        <DetailsTab
          tabs={availableTabs}
          value={resolvedTab}
          onChange={handleTabChange}
        />
      </div>

      {resolvedTab === 'taskInfo' && (
        <div className={styles.tabContent}>
          {showLocation && (
            <TaskLocation
              container={container as HTMLDivElement}
              taskPath={task.url || ''}
              absoluteUrl={absoluteUrl}
              canEditTaskUrl={accessTo.editTaskUrl}
              canManageProject={accessTo.manageProject}
              updateSite={(site: string) => updateTask({ site })}
              updateUrl={(url: string) => updateTask({ url })}
              taskId={Number(task.id)}
              taskHost={task.site || ''}
              sites={[]}
              devurl={projectContext?.devurl || ''}
              projectDomains={projectContext?.domains || []}
              pageUrl=""
              projectId={Number(task.project.id)}
              bugherdUrl={bugherdUrl}
              gotoTask={event => {
                event.preventDefault();
                window.open(absoluteUrl, '_blank', 'noreferrer');
              }}
            />
          )}
          <Severity
            priorityId={Number(task.priorityId || 0)}
            updateSeverity={(priorityId: number) => updateTask({ priorityId })}
            canEditSeverity={accessTo.taskEditSeverity}
          />
          {accessTo.taskEditVisibility && (
            <VisibleTo
              updateVisibility={(visibility: boolean) =>
                updateTask({ guestVisible: visibility })
              }
              guestVisible={!!task.guestVisible}
              hasGuestWatchers={hasGuestWatchers}
            />
          )}
          {(accessTo.taskEditAssignees ||
            accessTo.taskViewAssignees ||
            (accessTo.assignGuestsAllowed &&
              !!projectContext?.assignGuests)) && (
            <Assignees
              container={container as HTMLDivElement}
              canEditAssignees={accessTo.taskEditAssignees}
              viewAssignees={accessTo.taskViewAssignees}
              assignees={assignees}
              updateTask={updateTask}
              billing={accessTo.billing}
              assignableUsers={assignableUsers}
              assignGuests={!!projectContext?.assignGuests}
              assignGuestsAllowed={accessTo.assignGuestsAllowed}
              bugherdUrl={bugherdUrl}
              currentUser={
                { id: Number(currentUserId), role: currentUserRole } as any
              }
              guestVisible={!!task.guestVisible}
              guestsSeeGuestsAllowed={accessTo.guestsSeeGuestsAllowed}
              guestsSeeGuests={accessTo.guestsSeeGuests}
            />
          )}
          <Tags
            onUpdateTags={(ids: (number | null)[]) =>
              updateTask({
                tagIds: ids,
                tagNames: allTags
                  .filter(({ id }) => ids.includes(id))
                  .map(({ name }) => name)
              })
            }
            onCreateTag={createTag}
            onRemoveTag={(idToRemove: number | null) => {
              const remainingIds = taskTagIds.filter(id => id !== idToRemove);
              updateTask({
                tagIds: remainingIds,
                tagNames: allTags
                  .filter(({ id }) => remainingIds.includes(id))
                  .map(({ name }) => name)
              });
            }}
            tagSources={[]}
            tags={tags}
            tagIds={taskTagIds}
            allTags={allTags}
            canEditTags={accessTo.taskEditTags}
            canCreateTag={!!projectContext?.createTagsViaNewTask}
          />
          {(accessTo.taskEditDueDate || accessTo.taskViewDueDate) && (
            <DueDate
              dueDate={task.dueAt || ''}
              canEditDueDate={accessTo.taskEditDueDate}
              updateDueDate={(dueAt?: string) => updateTask({ dueAt })}
            />
          )}
          <AdditionalInfo
            browserData={(task.browserInfo || {}) as any}
            selector={(task.selectorInfo || {}) as any}
            absoluteUrl={absoluteUrl}
            canViewSelector={accessTo.taskViewSelector}
            metadata={task.data?.userMetaData as any}
            isAdminView
            isRecording={isRecordingScreenshot}
            hideDivider
          />
        </div>
      )}

      {resolvedTab === 'attachments' && showAttachments && (
        <div className={styles.tabContent}>
          <Attachments
            attachments={task.attachments.map(a => ({
              ...a,
              name: a.fileName,
              id: Number(a.id)
            }))}
            onUpdateAttachments={() => refetchTask()}
            bugherdUrl={bugherdUrl}
            apiDomain={bugherdUrl}
            projectId={Number(task.project.id)}
            taskId={Number(task.id)}
            container={container}
            pastedFiles={[]}
            hideDivider
            hideLabel
          />
        </div>
      )}

      {resolvedTab === 'comments' && (
        <div className={styles.tabContent}>
          <DrawerComments
            task={task}
            bugherdUrl={bugherdUrl}
            container={container}
            currentUserId={currentUserId}
            projectContext={projectContext}
            accessTo={accessTo}
            mentionableTasks={mentionableTasks}
            onOpenTask={onOpenTask}
          />
        </div>
      )}

      {resolvedTab === 'activity' && (
        <div className={styles.tabContent}>
          <DrawerActivityLog
            task={task}
            bugherdUrl={bugherdUrl}
            accessTo={accessTo}
          />
        </div>
      )}

      {resolvedTab === 'integrations' && showIntegrationsTab && (
        <div className={styles.tabContent}>
          <Integrations
            connectedIntegrations={connectedIntegrations}
            harvestIntegration={harvestIntegration}
            harvestEmbedUrl={harvestEmbedUrl}
            taskConnectionData={taskConnectionData}
            webIntegrations={webIntegrations}
            container={container as HTMLDivElement}
            projectId={projectId}
            organizationId={organizationId}
            bugherdUrl={bugherdUrl}
            apiDomain={bugherdUrl}
            taskId={taskId}
            taskLocalId={taskLocalId}
            isAdminView
            isSmallView
          />
        </div>
      )}
    </>
  );
};
